import { useCallback, useEffect, useReducer } from 'react'
import type { GeometryResponse, MediaAssetPublic, RoutePublic, Waypoint } from '@xingroute/schema'
import { ApiError, api } from '../../api/client.js'
import { useAuth } from '../../auth/AuthContext.js'
import { useLocale } from '../../locale/LocaleContext.js'
import { friendlyError } from '../../lib/friendlyError.js'

interface RouteDetailTarget {
  id?: string
  slug?: string
}

interface LoaderState {
  route: RoutePublic | null
  etag: string | null
  media: MediaAssetPublic[]
  geometry: GeometryResponse | null
  loading: boolean
  geometryLoading: boolean
  geometryError: string | null
  error: unknown
}

type LoaderAction =
  | { type: 'load/start' }
  | { type: 'load/finish' }
  | { type: 'load/route'; route: RoutePublic; etag: string | null }
  | { type: 'load/media'; media: MediaAssetPublic[] }
  | { type: 'load/geometry-start' }
  | { type: 'load/geometry-success'; geometry: GeometryResponse }
  | { type: 'load/geometry-error'; error: string }
  | { type: 'load/geometry-empty' }
  | { type: 'load/error'; error: unknown }
  | { type: 'route/set'; route: RoutePublic; etag: string | null }
  | { type: 'geometry/set'; geometry: GeometryResponse | null }
  | { type: 'media/set'; media: MediaAssetPublic[] }
  | { type: 'media/append'; media: MediaAssetPublic }
  | { type: 'media/remove'; mediaId: string }
  | { type: 'error/set'; error: unknown }
  | { type: 'waypoints/committed'; etag: string; waypoints: Waypoint[] }

const initialLoaderState: LoaderState = {
  route: null,
  etag: null,
  media: [],
  geometry: null,
  loading: false,
  geometryLoading: false,
  geometryError: null,
  error: null as unknown,
}

function reducer(state: LoaderState, action: LoaderAction): LoaderState {
  switch (action.type) {
    case 'load/start':
      return { ...state, loading: true, error: null, geometryError: null }
    case 'load/finish':
      return { ...state, loading: false }
    case 'load/route':
      return { ...state, route: action.route, etag: action.etag ?? action.route.etag }
    case 'load/media':
      return { ...state, media: action.media }
    case 'load/geometry-start':
      return { ...state, geometryLoading: true, geometryError: null }
    case 'load/geometry-success':
      return { ...state, geometry: action.geometry, geometryLoading: false, geometryError: null }
    case 'load/geometry-error':
      return { ...state, geometry: null, geometryLoading: false, geometryError: action.error }
    case 'load/geometry-empty':
      return { ...state, geometry: null, geometryLoading: false, geometryError: null }
    case 'load/error':
      return { ...state, loading: false, error: action.error }
    case 'route/set':
      return {
        ...state,
        // `created_by` is immutable, so the creator's resolved display name
        // never changes. Some mutation endpoints return routes via
        // `routeToPublic` (no creator resolution — e.g. approve-proposal,
        // publish/archive status transitions), which would blank out
        // `creator_name`/`creator_username` and make the collaboration
        // timeline's "created route" event fall back to a bare id. Carry the
        // values over from the prior route so the creator name persists.
        route: {
          ...action.route,
          creator_name: action.route.creator_name ?? state.route?.creator_name ?? null,
          creator_username: action.route.creator_username ?? state.route?.creator_username ?? null,
        },
        etag: action.etag ?? action.route.etag,
      }
    case 'geometry/set':
      return {
        ...state,
        geometry: action.geometry,
        geometryError: action.geometry ? null : state.geometryError,
      }
    case 'media/set':
      return { ...state, media: action.media }
    case 'media/append':
      return { ...state, media: [...state.media, action.media] }
    case 'media/remove':
      return { ...state, media: state.media.filter((m) => m.id !== action.mediaId) }
    case 'error/set':
      return { ...state, error: action.error }
    case 'waypoints/committed':
      return {
        ...state,
        etag: action.etag,
        geometry: state.geometry
          ? {
              ...state.geometry,
              etag: action.etag,
              points: action.waypoints,
            }
          : state.geometry,
        route: state.route ? { ...state.route, etag: action.etag } : state.route,
      }
  }
}

export function useRouteDetailLoader(target: RouteDetailTarget) {
  const [state, dispatch] = useReducer(reducer, initialLoaderState)
  const auth = useAuth()
  const { t } = useLocale()
  const token = auth.status === 'authed' ? auth.token : null

  const reload = useCallback(async () => {
    if (auth.status === 'loading') return
    try {
      dispatch({ type: 'load/start' })
      const result = target.slug
        ? await api.getRouteBySlug(target.slug, token)
        : await api.getRoute(target.id ?? '', token)
      dispatch({ type: 'load/route', route: result.route, etag: result.etag })
      const media = await api.listMedia(result.route.id, undefined, token)
      dispatch({ type: 'load/media', media })
      if (result.route.geometry_url) {
        dispatch({ type: 'load/geometry-start' })
        try {
          const { geometry } = await api.getRouteGeometry(result.route.id, token)
          dispatch({ type: 'load/geometry-success', geometry })
        } catch (err) {
          if (err instanceof ApiError && err.code === 'no_geometry') {
            dispatch({ type: 'load/geometry-empty' })
            return
          }
          dispatch({
            type: 'load/geometry-error',
            error: friendlyError(err, t),
          })
        }
      } else {
        dispatch({ type: 'load/geometry-empty' })
      }
    } catch (err) {
      dispatch({ type: 'load/error', error: err })
    } finally {
      dispatch({ type: 'load/finish' })
    }
  }, [auth.status, target.id, target.slug, token, t])

  useEffect(() => {
    if (auth.status === 'loading') return
    let cancelled = false
    async function load() {
      try {
        dispatch({ type: 'load/start' })
        const result = target.slug
          ? await api.getRouteBySlug(target.slug, token)
          : await api.getRoute(target.id ?? '', token)
        if (cancelled) return
        dispatch({ type: 'load/route', route: result.route, etag: result.etag })
        const media = await api.listMedia(result.route.id, undefined, token)
        if (cancelled) return
        dispatch({ type: 'load/media', media })
        if (result.route.geometry_url) {
          dispatch({ type: 'load/geometry-start' })
          try {
            const { geometry } = await api.getRouteGeometry(result.route.id, token)
            if (!cancelled) dispatch({ type: 'load/geometry-success', geometry })
          } catch (err) {
            if (!cancelled) {
              if (err instanceof ApiError && err.code === 'no_geometry') {
                dispatch({ type: 'load/geometry-empty' })
                return
              }
              dispatch({
                type: 'load/geometry-error',
                error: friendlyError(err, t),
              })
            }
          }
        } else {
          dispatch({ type: 'load/geometry-empty' })
        }
      } catch (err) {
        if (!cancelled) {
          dispatch({
            type: 'load/error',
            error: err,
          })
        }
      } finally {
        if (!cancelled) dispatch({ type: 'load/finish' })
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [auth.status, target.id, target.slug, token, t])

  return {
    route: state.route,
    etag: state.etag,
    media: state.media,
    geometry: state.geometry,
    loading: state.loading || state.geometryLoading,
    geometryError: state.geometryError,
    error: state.error,
    reload,
    setRoute(route: RoutePublic, etag: string | null) {
      dispatch({ type: 'route/set', route, etag })
    },
    setGeometry(geometry: GeometryResponse | null) {
      dispatch({ type: 'geometry/set', geometry })
    },
    appendMedia(media: MediaAssetPublic) {
      dispatch({ type: 'media/append', media })
    },
    setMedia(media: MediaAssetPublic[]) {
      dispatch({ type: 'media/set', media })
    },
    removeMedia(mediaId: string) {
      dispatch({ type: 'media/remove', mediaId })
    },
    setError(error: unknown) {
      dispatch({ type: 'error/set', error })
    },
    applyWaypointCommit(etag: string, waypoints: Waypoint[]) {
      dispatch({ type: 'waypoints/committed', etag, waypoints })
    },
  }
}
